import React, { ReactNode, ButtonHTMLAttributes, useState } from "react";
import { buttonSizes } from "./utils/sizes";
import { buttonVariants } from "./utils/variants";
import { BiLoaderAlt } from "react-icons/bi";

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  onClick?: () => void | Promise<void>;
  children: ReactNode;
  isLoading?: boolean;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  size?: "small" | "medium";
  variant?:
    | "primary"
    | "secondary"
    | "outline"
    | "ghost"
    | "danger"
    | "success"
    | "cancel";
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  className?: string;
  ariaLabel?: string;
  animateOnClick?: boolean;
}

const Button = ({
  onClick,
  children,
  isLoading = false,
  disabled = false,
  type = "button",
  size = "medium",
  variant = "primary",
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = "",
  ariaLabel,
  animateOnClick = true,
  ...rest
}: ButtonProps) => {
  const [isPressed, setIsPressed] = useState(false);
  const [ripples, setRipples] = useState<
    { x: number; y: number; id: number }[]
  >([]);

  const sizeClass = buttonSizes[size];
  const variantClass = buttonVariants[variant];
  const isDisabled = disabled || isLoading;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled || !onClick) return;

    // Create ripple effect
    if (animateOnClick) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const rippleId = Date.now();

      setRipples((prev) => [...prev, { x, y, id: rippleId }]);
      setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== rippleId));
      }, 600);
    }

    onClick();
  };

  const handleMouseDown = () => {
    if (animateOnClick && !isDisabled) {
      setIsPressed(true);
    }
  };

  const handleMouseUp = () => {
    setIsPressed(false);
  };

  return (
    <button
      disabled={isDisabled}
      type={type}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      aria-label={ariaLabel}
      className={`
        relative overflow-hidden
        font-semibold rounded-xl 
        flex justify-center items-center gap-2
        transition-all duration-300 ease-in-out
        transform
        ${sizeClass}
        ${variantClass}
        ${fullWidth ? "w-full" : ""}
        ${isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
        ${
          !isDisabled && animateOnClick
            ? "hover:scale-[1.02] active:scale-[0.98]"
            : ""
        }
        ${isPressed && animateOnClick ? "scale-[0.98]" : ""}
        ${!isDisabled ? "hover:shadow-lg" : ""}
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary
        ${className}
      `}
      {...rest}
    >
      {/* Ripple effects */}
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute rounded-full bg-white/30 pointer-events-none animate-ripple"
          style={{
            left: ripple.x,
            top: ripple.y,
            width: 0,
            height: 0,
          }}
        />
      ))}

      {/* Loading spinner */}
      {isLoading && (
        <BiLoaderAlt
          className="animate-spin transition-opacity duration-200"
          size={size === "small" ? 16 : 20}
        />
      )}

      {/* Left icon */}
      {!isLoading && leftIcon && (
        <span className="transition-transform duration-200 group-hover:scale-110">
          {leftIcon}
        </span>
      )}

      {/* Button content */}
      <span
        className={`
          transition-opacity duration-200
          ${isLoading ? "opacity-0 w-0 h-0 overflow-hidden" : "opacity-100"}
        `}
      >
        {children}
      </span>

      {/* Right icon */}
      {!isLoading && rightIcon && (
        <span className="transition-transform duration-200 group-hover:scale-110">
          {rightIcon}
        </span>
      )}
    </button>
  );
};

export default Button;
